import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { BpeVariableData } from "./bpe.vars.component";
import { S25ItemI } from "../../pojo/S25ItemI";
import { BpeVarsUtil } from "./s25.bpe.vars.util";
import { BpeVarsConst } from "./s25.bpe.const";
import { Doc } from "../system-settings/document-management/s25.document.const";
import { VariableWithOptions } from "../s25-dropdown/single-select/s25.bpe.vars.dropdown.component";
import { DropDownItem } from "../../pojo/DropDownItem";

@Component({
    selector: "s25-ng-bpe-variable",
    template: `
        @if (state) {
            @if (type || types) {
                <s25-bpe-vars-dropdown
                    class="c-margin-top--quarter"
                    [type]="type"
                    [types]="types"
                    [dataTypes]="dataTypes"
                    [(chosen)]="state.variable"
                    (chosenChange)="setVariable(state)"
                    [documentType]="documentType"
                    [hasNoneItem]="hasNoneItem"
                    [placeholder]="placeholder"
                    [itemFilter]="itemFilter"
                    [scrollIntoView]="scrollIntoView"
                ></s25-bpe-vars-dropdown>
            }
            @if (state.variable?.options) {
                <div class="options">
                    @for (option of options; track option) {
                        @if (!option.hidden) {
                            @switch (option.type) {
                                @case ("checkbox") {
                                    <s25-ng-checkbox
                                        [(modelValue)]="state.options[option.id]"
                                        (modelValueChange)="setVariable(state)"
                                        [attr.data-id]="option.id"
                                    >
                                        {{ option.label }}
                                    </s25-ng-checkbox>
                                }
                                @case ("dropdown") {
                                    <s25-generic-dropdown
                                        [items]="option.options"
                                        [(chosen)]="state.options[option.id]"
                                        (chosenChange)="setVariable(state)"
                                    />
                                }
                                @case ("roleDropdown") {
                                    <s25-ng-role-dropdown
                                        class="c-margin-top--quarter"
                                        [type]="option.data.type"
                                        [(chosen)]="state.options[option.id]"
                                        (chosenChange)="setVariable(state)"
                                    />
                                }
                                @case ("searchCriteriaItems") {
                                    <s25-ng-multiselect-search-criteria
                                        [type]="option.data.criterion"
                                        [modelBean]="{}"
                                        [popoverOnBody]="true"
                                        [showResults]="true"
                                        (changed)="state.options[option.id] = joinItemIds($event); setVariable(state)"
                                    />
                                }
                                @case ("searchCriteriaItem") {
                                    <s25-ng-dropdown-search-criteria
                                        [type]="option.data.criterion"
                                        [(chosen)]="state.options[option.id]"
                                        (chosenChange)="setVariable(state)"
                                    />
                                }
                            }
                        }
                    }
                </div>
            }
            @if (state.variable?.hasFormat && hasFormat) {
                <label class="c-margin-top--quarter">
                    <span class="selectedVarLabel">Format: </span>
                    <s25-bpe-vars-dropdown
                        type="dateFormats"
                        [(chosen)]="state.format"
                        (chosenChange)="setVariable(state)"
                        [documentType]="documentType"
                        [placeholder]="'Format'"
                    ></s25-bpe-vars-dropdown>
                </label>
            }
            @if (state.variable?.hasFormat && state.format && hasFormatExample) {
                <div>Format Example: {{ state.format.ex }}</div>
            }

            @if (hasPost) {
                <s25-ng-copy-variable [label]="'Variable'" [text]="state.var" />
            }
            @if (hasPre) {
                <s25-ng-copy-variable [label]="'Pre Variable'" [text]="state.preVar" />
            }
        }
    `,
    styles: `
        .options s25-ng-checkbox {
            margin-right: 0.5em;
        }

        ::ng-deep
            s25-ng-bpe-variable
            .options
            s25-ng-checkbox[data-id="includeAll"]:has(input[type="checkbox"]:checked)
            + s25-ng-multiselect-search-criteria {
            display: none;
        }

        .options s25-ng-multiselect-search-criteria {
            display: block;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25BpeVariableComponent implements OnChanges {
    @Input({ required: true }) state: BpeVariableData;
    @Input() type: keyof typeof BpeVarsConst.Items;
    @Input() types: (keyof typeof BpeVarsConst.Items)[];
    @Input({ required: false }) codeMode: boolean = false;
    @Input({ required: true }) documentType: Doc.TypeId = "event";
    @Input({ required: false }) hasPre: boolean;
    @Input({ required: false }) hasPost: boolean = true;
    @Input({ required: false }) hasFormat: boolean = true;
    @Input({ required: false }) hasFormatExample: boolean = true;
    @Input({ required: false }) prefix: boolean = true;
    @Input({ required: false }) wrap: boolean = true;
    @Input({ required: false }) hasNoneItem: boolean = false;
    @Input({ required: false }) itemFilter: (items: DropDownItem[]) => Promise<DropDownItem[]>;
    @Input({ required: false }) placeholder: string = "Variable";
    @Input({ required: false }) scrollIntoView: boolean;
    @Input({ required: false }) dataTypes: string[];

    @Output() variableChange = new EventEmitter<string>();

    options: VariableWithOptions["options"] = [];

    constructor(private cd: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.state || changes.type || changes.documentType) {
            this.setVariable(this.state, false);
        }
    }

    setVariable(state: BpeVariableData, updateVariable = true) {
        if (!state) return;
        this.setOptions(state);
        BpeVarsUtil.setVariable(state, this.codeMode, this.prefix, this.wrap);
        if (this.hasNoneItem && !state.variable) {
            state.var = undefined;
            state.preVar = undefined;
        }
        this.cd.detectChanges();
        if (updateVariable) this.variableChange.emit(state.var);
    }

    setOptions(state: BpeVariableData) {
        this.options = [];
        for (const option of state?.variable?.options || []) {
            const include = !option.documentTypes || option.documentTypes.includes(this.documentType);
            if (include) this.options.push(option);
            else delete state.options[option.id]; // If option is not present, delete its value
        }
        this.options = (state?.variable?.options || []).filter(
            (option) => !option.documentTypes || option.documentTypes.includes(this.documentType),
        );
    }

    joinItemIds(items: S25ItemI[]) {
        return items.map((item) => item.itemId).join(",");
    }
}
